import {
  Button,
  Checkbox,
  Fade,
  FormControl,
  FormHelperText,
  Grid,
  IconButton,
  InputLabel,
  ListItemText,
  MenuItem,
  OutlinedInput,
  Select,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import { Box } from "@mui/system";
import { DesktopDatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { DemoContainer } from "@mui/x-date-pickers/internals/demo";
import { closeIcon } from "assets/images";
import CalendarIcon from "components/CalendarIcon";
import CheckboxIcon from "components/CheckBoxIcon";
import CheckedBoxIcon from "components/CheckedBoxIcon";
import OverlayBox from "components/OverlayBox";
import { isEqual } from "lodash";
import { useEffect, useState } from "react";
import { Controller, FieldError, useForm } from "react-hook-form";
import {
  PLAUserListPayload,
  PLAUserListSuccessPayload,
} from "store/slpDashboard/type";
import {
  CLOSE_TOOLTIP_TITLE,
  DATE_PICKER_FORMAT,
  DEFAULT_PAGE_NO,
  MAX_BYTE_VALUE,
} from "utils/constants/constant";
import {
  DEFAULT_SLP_REQUESTS_FILTER,
  SLP_PARTICIPATION,
} from "utils/constants/slp";
import {
  hideLoaderForBatchApiCall,
  showLoaderForBatchApiCall,
  toggleFilter,
} from "utils/helper";
import { userRenderValue } from "utils/helper/slp";
import {
  getNumberOfCompletedSLPError,
  getSLPParticipationError,
} from "utils/helper/validation-message-helper";
import { IPLAUserOption, ISLPRequestsFilter } from "utils/interfaces/slp";

interface ISLPRequestsFilterProps {
  filterBox: boolean;
  setFilterBox: React.Dispatch<React.SetStateAction<boolean>>;
  handleApplyClick: (formData: ISLPRequestsFilter) => void;
  filteredData: ISLPRequestsFilter;
  pageNo: number;
  setPageNo: React.Dispatch<React.SetStateAction<number>>;
  setFilteredData: React.Dispatch<React.SetStateAction<ISLPRequestsFilter>>;
  getSLPRequests: (
    filterData: ISLPRequestsFilter,
    filtered?: boolean,
    pageNum?: number
  ) => void;
  setAppliedFilter: React.Dispatch<React.SetStateAction<boolean>>;
  plaUserListRequest: (payload: PLAUserListPayload) => {};
  isForCompletedSLP?: boolean;
}

const SLPRequestsFilter: React.FC<ISLPRequestsFilterProps> = ({
  filterBox,
  setFilterBox,
  handleApplyClick,
  filteredData,
  pageNo,
  setPageNo,
  setFilteredData,
  getSLPRequests,
  setAppliedFilter,
  plaUserListRequest,
  isForCompletedSLP = false,
}) => {
  const [userList, setUserList] = useState<Array<IPLAUserOption>>([]);

  const {
    handleSubmit,
    reset,
    register,
    setValue,
    watch,
    control,
    formState: { errors },
  } = useForm<ISLPRequestsFilter>({
    defaultValues: DEFAULT_SLP_REQUESTS_FILTER,
  });

  const onGetPLAUserListSuccess = (response: PLAUserListSuccessPayload) => {
    setUserList(response.userList);
    hideLoaderForBatchApiCall();
  };

  const getPLAUserList = () => {
    showLoaderForBatchApiCall();
    const payload = {
      callback: onGetPLAUserListSuccess,
    };

    plaUserListRequest(payload);
  };

  useEffect(() => {
    if (filterBox) getPLAUserList();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterBox]);

  useEffect(() => {
    reset(filteredData ?? DEFAULT_SLP_REQUESTS_FILTER);
  }, [filteredData, setValue, filterBox, reset]);

  const onClearClick = () => {
    reset();
    setFilterBox(false);
    setAppliedFilter(false);
    document.body.classList.toggle("filter-open");
    if (pageNo === DEFAULT_PAGE_NO) {
      getSLPRequests(DEFAULT_SLP_REQUESTS_FILTER);
    } else {
      setPageNo(DEFAULT_PAGE_NO);
    }
    setFilteredData(DEFAULT_SLP_REQUESTS_FILTER);
  };

  return (
    <>
      <form onSubmit={handleSubmit(handleApplyClick)}>
        {filterBox && <OverlayBox setFilterBox={setFilterBox} />}
        <Box className="filter-popover">
          <Box className="filter-header">
            <Typography variant="h4">Filter</Typography>
            <Tooltip
              TransitionComponent={Fade}
              TransitionProps={{ timeout: 600 }}
              title={CLOSE_TOOLTIP_TITLE}
              arrow
            >
              <IconButton
                onClick={() => {
                  setFilterBox(false);
                  toggleFilter();
                }}
              >
                <img src={closeIcon} alt="close" />
              </IconButton>
            </Tooltip>
          </Box>
          <Box className="filter-body">
            <FormControl fullWidth className="select">
              <InputLabel
                id="slp-participation-select-label"
                shrink={!!watch("slpParticipation")}
              >
                SLP Participation
              </InputLabel>
              <Controller
                name="slpParticipation"
                control={control}
                rules={{ required: true }}
                render={({ field }) => (
                  <Select
                    {...field}
                    labelId="slp-participation-select-label"
                    id="slp-participation-select"
                    value={field.value}
                    displayEmpty
                    multiple
                    renderValue={(selected) =>
                      SLP_PARTICIPATION.filter((option) =>
                        selected?.includes(option.value)
                      )
                        .map((option) => option.label)
                        .join(", ")
                    }
                  >
                    {SLP_PARTICIPATION?.map((participation) => (
                      <MenuItem
                        key={participation.value}
                        value={participation.value}
                      >
                        <Checkbox
                          checked={
                            field.value?.includes(participation.value) ?? false
                          }
                          icon={<CheckboxIcon />}
                          checkedIcon={<CheckedBoxIcon />}
                        />
                        <ListItemText primary={participation.label} />
                      </MenuItem>
                    ))}
                  </Select>
                )}
              />
              {!!errors.slpParticipation && (
                <FormHelperText error>
                  {getSLPParticipationError(
                    errors.slpParticipation as FieldError
                  )}
                </FormHelperText>
              )}
            </FormControl>
            <TextField
              id="completed-slp"
              label="No. of Completed SLPs"
              type="number"
              fullWidth
              variant="outlined"
              {...register("numberOfCompletedSLP", {
                max: MAX_BYTE_VALUE,
                setValueAs: (value) =>
                  value === "" || value === null ? null : Number(value),
              })}
              error={!!errors.numberOfCompletedSLP}
              helperText={getNumberOfCompletedSLPError(
                errors.numberOfCompletedSLP
              )}
            />
            <FormControl fullWidth className="select">
              <InputLabel id="allocated-to-select-label">
                Allocated To
              </InputLabel>
              <Select
                labelId="allocated-to-select-label"
                id="user-select"
                multiple
                label="Allocated To"
                input={<OutlinedInput label="Allocated To" />}
                renderValue={(selected) => userRenderValue(selected, userList)}
                value={watch("allocatedTo")}
                {...register("allocatedTo")}
              >
                {userList?.map((user) => (
                  <MenuItem key={user.id} value={user.id}>
                    <Checkbox
                      checked={watch("allocatedTo")?.indexOf(user.id) > -1}
                      icon={<CheckboxIcon />}
                      checkedIcon={<CheckedBoxIcon />}
                    />
                    <ListItemText primary={user.name} />
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <Grid container columnSpacing="12px">
              <Grid item xs={12} md={6}>
                <LocalizationProvider dateAdapter={AdapterDayjs}>
                  <DemoContainer components={["DesktopDatePicker"]}>
                    <Controller
                      control={control}
                      {...register("from")}
                      render={({ field }) => (
                        <DesktopDatePicker
                          {...field}
                          className="date-picker disabled-picker-text-field"
                          format={DATE_PICKER_FORMAT}
                          label={
                            isForCompletedSLP
                              ? "Completed From"
                              : "Due Date From"
                          }
                          slots={{
                            openPickerIcon: CalendarIcon,
                          }}
                          slotProps={{
                            textField: {
                              disabled: true,
                            },
                          }}
                        />
                      )}
                    />
                  </DemoContainer>
                </LocalizationProvider>
              </Grid>
              <Grid item xs={12} md={6}>
                <LocalizationProvider dateAdapter={AdapterDayjs}>
                  <DemoContainer components={["DesktopDatePicker"]}>
                    <Controller
                      control={control}
                      {...register("to")}
                      render={({ field }) => (
                        <DesktopDatePicker
                          {...field}
                          className="date-picker disabled-picker-text-field"
                          format={DATE_PICKER_FORMAT}
                          label={
                            isForCompletedSLP ? "Completed To" : "Due Date To"
                          }
                          slots={{
                            openPickerIcon: CalendarIcon,
                          }}
                          slotProps={{
                            textField: {
                              disabled: true,
                            },
                          }}
                        />
                      )}
                    />
                  </DemoContainer>
                </LocalizationProvider>
              </Grid>
            </Grid>
          </Box>
          <Box className="filter-footer">
            <Button variant="contained" className="btn-apply" type="submit">
              Apply
            </Button>
            <Button
              variant="outlined"
              className="btn-clear"
              onClick={onClearClick}
              disabled={isEqual(DEFAULT_SLP_REQUESTS_FILTER, watch())}
            >
              Clear
            </Button>
          </Box>
        </Box>
      </form>
    </>
  );
};

export default SLPRequestsFilter;
